try {
(() => {
  const key = Symbol.for('pernoire.productInteractions');
  if (window[key]) { window[key].initialize(document); return; }
  const controllers = new Map();
  const restoreAttribute = (element, name, value) => {
    if (value === null) element.removeAttribute(name);
    else element.setAttribute(name, value);
  };

  class ProductInteractions {
    constructor(root) {
      this.root = root;
      this.stickyStory = root.hasAttribute('data-pdp-sticky-story');
      this.originalSidebarTop = root.style.getPropertyValue('--nx-pdp-sidebar-top');
      this.gallery = root.querySelector('[data-pdp-gallery]');
      this.frame = root.querySelector('[data-pdp-frame]');
      this.images = Array.from(this.frame?.querySelectorAll('.nx-product__image') || []);
      this.thumbs = Array.from(this.gallery?.querySelectorAll('[data-pdp-thumb]') || []);
      this.dialog = root.querySelector('[data-pdp-dialog]');
      this.zoomImage = root.querySelector('[data-pdp-zoom-image]');
      this.zoomButtons = Array.from(root.querySelectorAll('[data-pdp-zoom]'));
      this.controls = Array.from(root.querySelectorAll('[data-pdp-control]'));
      this.sticky = root.querySelector('[data-pdp-sticky]');
      this.stickyButton = root.querySelector('[data-pdp-sticky-button]');
      this.stickyLabel = root.querySelector('[data-pdp-sticky-label]');
      this.stickyTitle = root.querySelector('[data-pdp-sticky-title]');
      this.stickyPrice = root.querySelector('[data-pdp-sticky-price]');
      this.stickyError = root.querySelector('[data-pdp-sticky-error]');
      this.buy = root.querySelector('[data-pdp-buy]');
      this.mainButton = root.querySelector('form[data-nx-add] [name="add"]');
      this.formError = root.querySelector('form[data-nx-add] [data-nx-error]');
      this.price = root.querySelector('.nx-product [data-nx-price]');
      this.variant = root.querySelector('[data-nx-variant]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 760px)');
      this.events = new AbortController();
      this.disposed = false;
      this.layoutFrame = 0;
      this.pointer = null;
      this.zoomTrigger = null;
      this.zoomRequest = 0;
      this.zoomLoader = null;
      this.index = Math.max(0, this.images.findIndex(image => image.classList.contains('nx-image-selected')));
      this.originalReady = root.getAttribute('data-pdp-ready');
      this.originalControls = this.controls.map(control => [control, control.hidden]);
      this.originalImages = this.images.map(image => [image, image.classList.contains('nx-image-selected'), image.getAttribute('aria-hidden')]);
      this.originalThumbs = this.thumbs.map(thumb => [thumb, thumb.getAttribute('aria-current')]);
      const options = { signal: this.events.signal };
      root.setAttribute('data-pdp-ready', 'true');
      this.controls.forEach(control => { control.hidden = false; });
      root.querySelectorAll('[data-pdp-prev], [data-pdp-next]').forEach(button => { button.disabled = this.images.length < 2; });
      this.canZoom = !!(this.dialog && this.zoomImage && typeof this.dialog.showModal === 'function' && this.images.length);
      this.zoomButtons.forEach(button => {
        button.hidden = !this.canZoom;
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-expanded', 'false');
        if (this.dialog?.id) button.setAttribute('aria-controls', this.dialog.id);
      });
      if (this.frame) {
        this.originalTouchAction = this.frame.style.touchAction;
        this.originalTabindex = this.frame.getAttribute('tabindex');
        this.frame.style.touchAction = 'pan-y pinch-zoom';
        if (this.images.length > 1 && this.originalTabindex === null) this.frame.setAttribute('tabindex', '0');
        this.frame.addEventListener('pointerdown', event => this.pointerDown(event), { ...options, passive: true });
        this.frame.addEventListener('pointerup', event => this.pointerUp(event), { ...options, passive: true });
        this.frame.addEventListener('pointercancel', () => { this.pointer = null; }, options);
      }
      root.addEventListener('click', event => this.click(event), options);
      root.addEventListener('keydown', event => this.keydown(event), options);
      this.variant?.addEventListener('change', () => {
        this.selectVariantImage(true);
        queueMicrotask(() => { if (!this.disposed) this.syncSticky(); });
      }, options);
      if (this.dialog) {
        this.dialog.addEventListener('close', () => this.closedDialog(), options);
        this.dialog.addEventListener('cancel', () => { this.pointer = null; }, options);
      }
      window.addEventListener('scroll', () => this.scheduleLayout(), { ...options, passive: true });
      window.addEventListener('resize', () => this.scheduleLayout(), { ...options, passive: true });
      window.addEventListener('pageshow', () => { this.syncSticky(); this.scheduleLayout(); }, options);
      document.addEventListener('close', () => this.scheduleLayout(), { ...options, capture: true });
      document.addEventListener('toggle', () => this.scheduleLayout(), { ...options, capture: true });
      this.stateObserver = new MutationObserver(() => this.syncSticky());
      if (this.mainButton) this.stateObserver.observe(this.mainButton, { attributes: true, attributeFilter: ['disabled', 'aria-busy'], childList: true, characterData: true, subtree: true });
      if (this.price) this.stateObserver.observe(this.price, { childList: true, characterData: true, subtree: true });
      if (this.formError) this.stateObserver.observe(this.formError, { childList: true, characterData: true, subtree: true });
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
        this.resizeObserver.observe(root);
        if (this.buy) this.resizeObserver.observe(this.buy);
        if (this.sticky) this.resizeObserver.observe(this.sticky);
        const header = document.querySelector('.nx-header');
        if (header) this.resizeObserver.observe(header);
      }
      if (this.buy && typeof IntersectionObserver === 'function') {
        this.buyObserver = new IntersectionObserver(() => this.scheduleLayout(), { threshold: [0, 1] });
        this.buyObserver.observe(this.buy);
      }
      if (this.sticky) { this.sticky.hidden = false; this.setStickyVisible(false); }
      this.select(this.index, false);
      this.selectVariantImage(false);
      this.syncSticky();
      this.scheduleLayout();
    }

    select(index, reveal = true) {
      if (!this.images.length || this.disposed) return;
      const previousIndex = this.index;
      this.index = (index + this.images.length) % this.images.length;
      const selected = this.images[this.index];
      const animateSelection = reveal && this.index !== previousIndex && !this.motion.matches;
      this.images.forEach(image => {
        const active = image === selected;
        image.classList.toggle('nx-image-selected', active);
        image.classList.toggle('nx-image-entering', active && animateSelection);
        image.setAttribute('aria-hidden', String(!active));
      });
      let activeThumb;
      this.thumbs.forEach(thumb => {
        const active = thumb.hash.slice(1) === selected.id;
        if (active) { thumb.setAttribute('aria-current', 'true'); activeThumb = thumb; }
        else thumb.removeAttribute('aria-current');
      });
      this.root.querySelectorAll('[data-pdp-current]').forEach(node => { node.textContent = String(this.index + 1); });
      this.root.querySelectorAll('[data-pdp-total]').forEach(node => { node.textContent = String(this.images.length); });
      if (this.dialog?.open) this.updateZoomImage();
      if (reveal && activeThumb) this.revealThumb(activeThumb);
    }

    revealThumb(thumb) {
      const rail = thumb.closest('.nx-product__thumbs') || thumb.parentElement;
      if (!rail || rail.scrollWidth <= rail.clientWidth) return;
      const railBounds = rail.getBoundingClientRect();
      const bounds = thumb.getBoundingClientRect();
      const delta = bounds.left < railBounds.left + 8 ? bounds.left - railBounds.left - 8
        : bounds.right > railBounds.right - 8 ? bounds.right - railBounds.right + 8 : 0;
      if (delta) rail.scrollBy({ left: delta, behavior: this.motion.matches ? 'instant' : 'smooth' });
    }

    selectVariantImage(reveal) {
      const imageId = this.variant?.selectedOptions[0]?.dataset.imageId;
      const index = imageId ? this.images.findIndex(image => image.id === imageId) : -1;
      if (index >= 0) this.select(index, reveal);
    }

    click(event) {
      if (!(event.target instanceof Element)) return;
      const thumb = event.target.closest('[data-pdp-thumb]');
      if (thumb && this.gallery?.contains(thumb)) {
        const index = this.images.findIndex(image => image.id === thumb.hash.slice(1));
        if (index >= 0) { event.preventDefault(); this.select(index); }
        return;
      }
      if (event.target.closest('[data-pdp-prev]')) { event.preventDefault(); this.select(this.index - 1); return; }
      if (event.target.closest('[data-pdp-next]')) { event.preventDefault(); this.select(this.index + 1); return; }
      const zoom = event.target.closest('[data-pdp-zoom]');
      if (zoom) { event.preventDefault(); this.openDialog(zoom); return; }
      if (event.target.closest('[data-pdp-close]')) { event.preventDefault(); if (this.dialog?.open) this.dialog.close(); return; }
      if (event.target === this.dialog && this.dialog.open) {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) {
          event.stopPropagation();
          this.dialog.close();
        }
      }
    }

    keydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !(event.target instanceof Element)) return;
      if (event.target.closest('input, select, textarea, [contenteditable="true"]')) return;
      const inDialog = this.dialog?.open && this.dialog.contains(event.target);
      if (!inDialog && !this.gallery?.contains(event.target)) return;
      let index;
      if (event.key === 'ArrowLeft') index = this.index - 1;
      else if (event.key === 'ArrowRight') index = this.index + 1;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.images.length - 1;
      else return;
      if (this.images.length < 2) return;
      event.preventDefault();
      this.select(index);
      if (event.target.closest('[data-pdp-thumb]')) {
        const active = this.thumbs.find(thumb => thumb.getAttribute('aria-current') === 'true');
        active?.focus({ preventScroll: true });
      }
    }

    pointerDown(event) {
      this.pointer = null;
      if (event.pointerType !== 'touch' || !event.isPrimary || this.images.length < 2) return;
      if (event.target instanceof Element && event.target.closest('a, button, input, select')) return;
      if (event.clientX < 24 || event.clientX > window.innerWidth - 24) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }

    pointerUp(event) {
      const start = this.pointer;
      this.pointer = null;
      if (!start || start.id !== event.pointerId) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) >= 44 && Math.abs(dx) > Math.abs(dy) * 1.5) this.select(this.index + (dx < 0 ? 1 : -1));
    }

    cancelZoomLoad() {
      this.zoomRequest++;
      if (!this.zoomLoader) return;
      this.zoomLoader.onload = this.zoomLoader.onerror = null;
      this.zoomLoader.removeAttribute('src');
      this.zoomLoader = null;
    }

    updateZoomImage() {
      const image = this.images[this.index];
      if (!image || !this.zoomImage || this.disposed) return;
      this.cancelZoomLoad();
      const preview = image.currentSrc || image.src;
      const source = image.dataset.fullSrc || preview;
      if (preview && this.zoomImage.getAttribute('src') !== preview) this.zoomImage.setAttribute('src', preview);
      this.zoomImage.alt = image.alt;
      if (!source || source === preview) return;
      const request = this.zoomRequest;
      const loader = new Image();
      this.zoomLoader = loader;
      loader.decoding = 'async';
      const complete = () => {
        loader.onload = loader.onerror = null;
        if (this.zoomLoader === loader) this.zoomLoader = null;
      };
      loader.onload = async () => {
        try { if (typeof loader.decode === 'function') await loader.decode(); }
        catch (_) { complete(); return; }
        if (!this.disposed && request === this.zoomRequest && this.images[this.index] === image && this.dialog?.open) {
          this.zoomImage.setAttribute('src', source);
        }
        complete();
      };
      // Keep the smaller, already selected photograph if the full-size copy fails.
      loader.onerror = complete;
      loader.src = source;
    }

    openDialog(trigger) {
      if (!this.canZoom || this.dialog.open) return;
      this.zoomTrigger = trigger;
      this.updateZoomImage();
      this.dialog.showModal();
      this.zoomButtons.forEach(button => button.setAttribute('aria-expanded', 'true'));
      this.dialog.querySelector('[data-pdp-close]')?.focus({ preventScroll: true });
      this.scheduleLayout();
    }

    closedDialog() {
      this.cancelZoomLoad();
      this.zoomButtons.forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (!this.disposed && this.zoomTrigger?.isConnected) this.zoomTrigger.focus({ preventScroll: true });
      this.zoomTrigger = null;
      this.scheduleLayout();
    }

    syncSticky() {
      if (this.disposed || !this.sticky || !this.mainButton || !this.stickyButton) return;
      if (this.stickyButton.disabled !== this.mainButton.disabled) this.stickyButton.disabled = this.mainButton.disabled;
      const busy = this.mainButton.getAttribute('aria-busy');
      if (this.stickyButton.getAttribute('aria-busy') !== busy) {
        if (busy === null) this.stickyButton.removeAttribute('aria-busy');
        else this.stickyButton.setAttribute('aria-busy', busy);
      }
      const label = this.mainButton.querySelector('[data-nx-add-label]');
      if (this.stickyLabel && label && this.stickyLabel.textContent !== label.textContent) this.stickyLabel.textContent = label.textContent;
      if (this.stickyPrice && this.price && this.stickyPrice.textContent !== this.price.textContent) this.stickyPrice.textContent = this.price.textContent;
      if (this.stickyTitle?.hasAttribute('data-pdp-sticky-variant')) {
        const variant = this.variant?.selectedOptions[0]?.dataset.variantTitle;
        const productTitle = this.stickyTitle.dataset.pdpStickyTitle;
        const title = variant ? variant + ' · 7.5 ml' : productTitle;
        if (this.stickyTitle.textContent !== title) this.stickyTitle.textContent = title;
        const accessibleLabel = [label?.textContent.trim(), productTitle, title].filter(Boolean).join(' · ');
        if (this.stickyButton.getAttribute('aria-label') !== accessibleLabel) this.stickyButton.setAttribute('aria-label', accessibleLabel);
      }
      if (this.stickyError && this.formError) {
        const message = this.formError.textContent.trim();
        if (this.stickyError.textContent !== message) this.stickyError.textContent = message;
        if (this.stickyError.hidden !== !message) this.stickyError.hidden = !message;
      }
      this.scheduleLayout();
    }

    setStickyVisible(visible) {
      if (!this.sticky) return;
      const visibleBars = Array.from(document.querySelectorAll('[data-pdp-sticky].is-visible')).filter(bar => bar !== this.sticky);
      if (visible) visibleBars.push(this.sticky);
      const height = visibleBars.length ? `${Math.ceil(Math.max(...visibleBars.map(bar => bar.getBoundingClientRect().height)))}px` : '';
      const hidden = String(!visible), anyVisible = visibleBars.length > 0;
      if (this.sticky.classList.contains('is-visible') !== visible) this.sticky.classList.toggle('is-visible', visible);
      if (this.sticky.getAttribute('aria-hidden') !== hidden) this.sticky.setAttribute('aria-hidden', hidden);
      if (this.sticky.inert !== !visible) this.sticky.inert = !visible;
      if (document.body.classList.contains('nx-pdp-sticky-visible') !== anyVisible) document.body.classList.toggle('nx-pdp-sticky-visible', anyVisible);
      if (document.body.style.getPropertyValue('--nx-pdp-sticky-height') !== height) {
        if (height) document.body.style.setProperty('--nx-pdp-sticky-height', height);
        else document.body.style.removeProperty('--nx-pdp-sticky-height');
      }
    }

    scheduleLayout() {
      if (this.disposed || this.layoutFrame) return;
      this.layoutFrame = requestAnimationFrame(() => {
        this.layoutFrame = 0;
        if (this.disposed || !this.sticky || !this.buy || !this.mainButton) return;
        const header = document.querySelector('.nx-header');
        const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
        const buyBounds = this.buy.getBoundingClientRect();
        const rootBounds = this.root.getBoundingClientRect();
        const sidebarTop = `${Math.ceil(headerBottom) + 16}px`;
        const sidebarChanged = this.stickyStory && this.root.style.getPropertyValue('--nx-pdp-sidebar-top') !== sidebarTop;
        const mobilePurchaseBar = !this.stickyStory || this.mobile.matches;
        const immersive = this.mobile.matches && this.root.dataset.pdpMobileLayout === 'immersive';
        const buyOutside = buyBounds.bottom <= headerBottom || (immersive && !this.root.hasAttribute('data-anelo-product') && buyBounds.top >= window.innerHeight);
        const footerTop = document.querySelector('footer.nx-footer')?.getBoundingClientRect().top ?? Infinity;
        const visible = this.root.dataset.pdpAvailable !== 'false' && mobilePurchaseBar && buyOutside && rootBounds.top < window.innerHeight && rootBounds.bottom > headerBottom + 80 && footerTop >= window.innerHeight && !document.querySelector('dialog[open]');
        this.setStickyVisible(visible);
        if (sidebarChanged) {
          this.root.style.setProperty('--nx-pdp-sidebar-top', sidebarTop);
          this.scheduleLayout();
        }
      });
    }

    dispose() {
      this.disposed = true;
      this.cancelZoomLoad();
      if (this.stickyStory) {
        if (this.originalSidebarTop) this.root.style.setProperty('--nx-pdp-sidebar-top', this.originalSidebarTop);
        else this.root.style.removeProperty('--nx-pdp-sidebar-top');
      }
      this.events.abort();
      this.stateObserver.disconnect();
      this.resizeObserver?.disconnect();
      this.buyObserver?.disconnect();
      cancelAnimationFrame(this.layoutFrame);
      if (this.dialog?.open) this.dialog.close();
      this.originalControls.forEach(([control, hidden]) => { control.hidden = hidden; });
      this.originalImages.forEach(([image, active, ariaHidden]) => {
        image.classList.toggle('nx-image-selected', active);
        image.classList.remove('nx-image-entering');
        restoreAttribute(image, 'aria-hidden', ariaHidden);
      });
      this.originalThumbs.forEach(([thumb, current]) => restoreAttribute(thumb, 'aria-current', current));
      if (this.frame) {
        this.frame.style.touchAction = this.originalTouchAction;
        restoreAttribute(this.frame, 'tabindex', this.originalTabindex);
      }
      restoreAttribute(this.root, 'data-pdp-ready', this.originalReady);
      this.setStickyVisible(false);
      if (this.sticky) this.sticky.hidden = true;
      this.pointer = null;
      this.zoomTrigger = null;
    }
  }

  const initialize = scope => {
    const roots = Array.from(scope.querySelectorAll('[data-pdp-root]'));
    if (scope.matches?.('[data-pdp-root]')) roots.unshift(scope);
    roots.forEach(root => {
      if (!controllers.has(root)) controllers.set(root, new ProductInteractions(root));
    });
  };
  const unload = scope => {
    for (const [root, controller] of controllers) {
      if (root === scope || scope.contains(root)) { controller.dispose(); controllers.delete(root); }
    }
  };
  window[key] = { initialize };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  window.addEventListener('pagehide', event => { if (!event.persisted) unload(document); });
})();
} catch (error) {
  if (typeof reportError === 'function') reportError(error);
  else setTimeout(() => { throw error; }, 0);
}

try {
(() => {
  const active = new Map();
  const revealed = new WeakSet();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const narrow = window.matchMedia('(max-width: 760px)');
  const selector = '[data-nx-product-story]';
  const destroy = (root) => {
    const controller = active.get(root);
    if (controller) controller.disconnect();
    root.querySelectorAll('[data-nx-story-reveal]').forEach((element) => {
      if (typeof element.getAnimations === 'function') element.getAnimations().forEach((animation) => animation.cancel());
    });
    active.delete(root);
  };
  const setup = (root) => {
    destroy(root);
    if (reduced.matches || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        if (revealed.has(entry.target)) return;
        revealed.add(entry.target);
        if (reduced.matches || typeof entry.target.animate !== 'function') return;
        entry.target.animate(
          [{ opacity: 0.84, transform: `translateY(${narrow.matches ? 8 : 12}px)` }, { opacity: 1, transform: 'translateY(0)' }],
          { duration: 500, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' }
        );
      });
    }, { threshold: 0, rootMargin: '0px 0px -32px 0px' });
    root.querySelectorAll('[data-nx-story-reveal]').forEach((element) => {
      if (!revealed.has(element)) observer.observe(element);
    });
    active.set(root, observer);
  };
  const boot = (scope) => {
    if (scope.matches && scope.matches(selector)) setup(scope);
    scope.querySelectorAll(selector).forEach(setup);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    Array.from(active.keys()).forEach((root) => {
      if (event.target === root || event.target.contains(root)) destroy(root);
    });
  });
  reduced.addEventListener('change', () => boot(document));
})();
} catch (error) {
  if (typeof reportError === 'function') reportError(error);
  else setTimeout(() => { throw error; }, 0);
}

